import { Component, inject, signal } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CalendarService } from '../../core/localization/calendar.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import {
  InsurancePolicyCoverage,
  InsurancePolicyListItem,
  InsurancePolicyPartyRole,
  InsurancePolicyTerm
} from './insurance-api.service';

export interface PolicyDraftPreviewContext {
  policy: InsurancePolicyListItem;
  coverages: InsurancePolicyCoverage[];
  partyRoles: InsurancePolicyPartyRole[];
  terms: InsurancePolicyTerm[];
  partyLabels: Record<string, string>;
  productDisplayName: string;
  lineOfBusinessLabel: string;
  statusLabel: string;
  renderedHtml?: string | null;
  templateName?: string | null;
}

@Component({
  standalone: true,
  selector: 'ain-insurance-policy-draft-preview',
  imports: [DialogModule, ButtonModule, AinAmountPipe, AinDatePipe],
  template: `
    <p-dialog
      [header]="t('insurance.policyDetail.draftPreview.title')"
      [modal]="true"
      [(visible)]="visible"
      [style]="{ width: 'min(920px, 96vw)' }"
      [contentStyle]="{ overflow: 'auto', maxHeight: '75vh' }">
      @if (context()) {
        @if (context()!.renderedHtml) {
          <div class="policy-template-doc">
            <div class="template-header">
              <span>{{ t('insurance.policyDetail.draftPreview.templateApplied') }}</span>
              <strong>{{ context()!.templateName || '-' }}</strong>
            </div>
            <iframe
              class="template-frame"
              [attr.srcdoc]="safeRenderedSrcdoc()"
              [title]="t('insurance.policyDetail.draftPreview.title')">
            </iframe>
          </div>
        } @else {
        <div class="policy-doc" [class.with-watermark]="showWatermark()">
          @if (showWatermark()) {
            <div class="watermark" aria-hidden="true">{{ t('insurance.policyDetail.draftPreview.watermark') }}</div>
          }
          <article class="policy-doc-body">
            <header class="doc-header">
              <h2>{{ t('insurance.policyDetail.draftPreview.documentTitle') }}</h2>
              <p class="doc-number">{{ context()!.policy.policyNumber }}</p>
            </header>

            <section class="doc-section">
              <h3>{{ t('insurance.policies.sections.info') }}</h3>
              <dl class="doc-grid">
                <div><dt>{{ t('columns.status') }}</dt><dd>{{ context()!.statusLabel }}</dd></div>
                <div><dt>{{ t('insurance.policies.fields.product') }}</dt><dd>{{ context()!.productDisplayName }}</dd></div>
                <div><dt>{{ t('insurance.policies.fields.lineOfBusiness') }}</dt><dd>{{ context()!.lineOfBusinessLabel }}</dd></div>
                <div><dt>{{ t('insurance.policies.fields.premium') }}</dt><dd>{{ context()!.policy.premiumAmount | ainAmount }} {{ context()!.policy.currencyCode }}</dd></div>
                <div><dt>{{ t('insurance.policies.fields.effectiveFrom') }}</dt><dd>{{ context()!.policy.effectiveFromUtc | ainDate }}</dd></div>
                <div><dt>{{ t('insurance.policies.fields.effectiveTo') }}</dt><dd>{{ context()!.policy.effectiveToUtc | ainDate }}</dd></div>
                @if (context()!.policy.issueDateUtc) {
                  <div><dt>{{ t('insurance.policyDetail.fields.issueDate') }}</dt><dd>{{ context()!.policy.issueDateUtc | ainDate }}</dd></div>
                }
              </dl>
            </section>

            <section class="doc-section">
              <h3>{{ t('insurance.policyDetail.sections.coverages') }}</h3>
              @if (context()!.coverages.length === 0) {
                <p class="muted">{{ t('insurance.policyDetail.states.noCoverages') }}</p>
              } @else {
                <table class="doc-table">
                  <thead>
                    <tr>
                      <th>{{ t('insurance.policyDetail.coverages.code') }}</th>
                      <th>{{ t('insurance.policyDetail.coverages.name') }}</th>
                      <th>{{ t('insurance.policyDetail.coverages.sumInsured') }}</th>
                      <th>{{ t('insurance.policyDetail.coverages.premium') }}</th>
                      <th>{{ t('insurance.policyDetail.coverages.deductible') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of context()!.coverages; track row.id || row.coverageCode) {
                      <tr>
                        <td>{{ row.coverageCode }}</td>
                        <td>{{ row.coverageName }}</td>
                        <td>{{ row.sumInsured | ainAmount }}</td>
                        <td>{{ row.premiumAmount | ainAmount }}</td>
                        <td>{{ row.deductibleAmount ?? '-' }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              }
            </section>

            <section class="doc-section">
              <h3>{{ t('insurance.policyDetail.sections.parties') }}</h3>
              @if (context()!.partyRoles.length === 0) {
                <p class="muted">{{ t('insurance.policyDetail.states.noParties') }}</p>
              } @else {
                <table class="doc-table">
                  <thead>
                    <tr>
                      <th>{{ t('insurance.policyDetail.parties.partyId') }}</th>
                      <th>{{ t('insurance.policyDetail.parties.role') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (row of context()!.partyRoles; track row.id || row.partyId) {
                      <tr>
                        <td>{{ partyLabel(row.partyId) }}</td>
                        <td>{{ partyRoleLabel(row.role) }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              }
            </section>

            @if (context()!.terms.length > 0) {
              <section class="doc-section">
                <h3>{{ t('insurance.policyDetail.sections.terms') }}</h3>
                <table class="doc-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{{ t('insurance.policies.fields.effectiveFrom') }}</th>
                      <th>{{ t('insurance.policies.fields.effectiveTo') }}</th>
                      <th>{{ t('insurance.policies.fields.premium') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (term of context()!.terms; track term.id) {
                      <tr>
                        <td>{{ term.termNumber }}</td>
                        <td>{{ term.fromUtc | ainDate }}</td>
                        <td>{{ term.toUtc | ainDate }}</td>
                        <td>{{ term.premiumAmount | ainAmount }} {{ term.currencyCode }}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </section>
            }
          </article>
        </div>
        }
      }

      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('insurance.policyDetail.draftPreview.print')" (click)="print()"></button>
        <button pButton type="button" severity="secondary" [label]="t('insurance.policyDetail.draftPreview.download')" (click)="download()"></button>
        <button pButton type="button" [label]="t('common.cancel')" (click)="visible.set(false)"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .policy-template-doc { border: 1px solid #e2e8f0; border-radius: 12px; overflow: hidden; background: #fff; }
    .template-header { display: flex; justify-content: space-between; gap: 8px; padding: 10px 14px; border-bottom: 1px solid #e2e8f0; background: #f8fafc; color: #334155; font-size: 13px; }
    .template-frame {
      display: block;
      width: 100%;
      min-height: 60vh;
      max-height: 60vh;
      border: 0;
      background: #fff;
    }
    .policy-doc { position: relative; background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; overflow: hidden; }
    .policy-doc.with-watermark .watermark {
      position: absolute;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      z-index: 1;
      font-size: clamp(48px, 12vw, 96px);
      font-weight: 700;
      color: rgba(185, 28, 28, 0.12);
      transform: rotate(-32deg);
      user-select: none;
      letter-spacing: 0.08em;
    }
    .policy-doc-body { position: relative; z-index: 2; }
    .doc-header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #1e3a8a; padding-bottom: 12px; }
    .doc-header h2 { margin: 0 0 6px; color: #1e3a8a; font-size: 22px; }
    .doc-number { margin: 0; font-size: 16px; font-weight: 600; color: #334155; }
    .doc-section { margin-top: 18px; }
    .doc-section h3 { margin: 0 0 10px; font-size: 15px; color: #1e40af; }
    .doc-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; margin: 0; }
    .doc-grid div { display: grid; gap: 2px; }
    .doc-grid dt { margin: 0; font-size: 12px; color: #64748b; }
    .doc-grid dd { margin: 0; font-weight: 600; color: #0f172a; }
    .doc-table { width: 100%; border-collapse: collapse; font-size: 13px; }
    .doc-table th, .doc-table td { border: 1px solid #e2e8f0; padding: 8px; text-align: start; }
    .doc-table th { background: #f8fafc; color: #334155; }
    .muted { color: #64748b; margin: 0; }
    @media (max-width: 640px) { .doc-grid { grid-template-columns: 1fr; } }
  `]
})
export class InsurancePolicyDraftPreviewComponent {
  private static readonly POLICY_DOC_FONT_STACK = '"Vazirmatn", "Segoe UI", Tahoma, sans-serif';
  private static readonly POLICY_DOC_FONT_ASSETS = [
    'https://cdn.jsdelivr.net/npm/@fontsource/vazirmatn@5.2.8/400.css',
    'https://cdn.jsdelivr.net/npm/@fontsource/vazirmatn@5.2.8/500.css',
    'https://cdn.jsdelivr.net/npm/@fontsource/vazirmatn@5.2.8/600.css',
    'https://cdn.jsdelivr.net/npm/@fontsource/vazirmatn@5.2.8/700.css'
  ];

  private readonly localization = inject(LocalizationService);
  private readonly calendar = inject(CalendarService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly visible = signal(false);
  readonly context = signal<PolicyDraftPreviewContext | null>(null);
  readonly safeRenderedSrcdoc = signal<SafeHtml | null>(null);

  private readonly partyRoleLabelKeys: Record<string, string> = {
    Policyholder: 'insurance.policyDetail.parties.roles.policyholder',
    Insured: 'insurance.policyDetail.parties.roles.insured',
    Beneficiary: 'insurance.policyDetail.parties.roles.beneficiary',
    Payer: 'insurance.policyDetail.parties.roles.payer',
    LossPayee: 'insurance.policyDetail.parties.roles.lossPayee'
  };

  open(context: PolicyDraftPreviewContext): void {
    this.context.set(context);
    this.safeRenderedSrcdoc.set(
      context.renderedHtml
        ? this.sanitizer.bypassSecurityTrustHtml(this.prepareTemplateDocument(context.renderedHtml))
        : null);
    this.visible.set(true);
  }

  showWatermark(): boolean {
    const status = this.context()?.policy.status;
    return status === 'Draft' || status === 'Bound';
  }

  partyLabel(partyId: string): string {
    const normalized = partyId?.trim();
    if (!normalized) {
      return '-';
    }
    return this.context()?.partyLabels[normalized] ?? normalized;
  }

  partyRoleLabel(role: string): string {
    const key = this.partyRoleLabelKeys[role];
    return key ? this.t(key) : role;
  }

  print(): void {
    const html = this.buildExportHtml();
    const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=1024,height=768');
    if (!printWindow) {
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
    setTimeout(() => {
      if (!printWindow.closed) {
        printWindow.print();
      }
    }, 400);
  }

  download(): void {
    const context = this.context();
    if (!context) {
      return;
    }
    const html = this.buildExportHtml();
    const suffix = this.showWatermark() ? '-draft' : '';
    const fileName = `${context.policy.policyNumber}${suffix}.html`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  private buildExportHtml(): string {
    const context = this.context();
    if (!context) {
      return '';
    }
    if (context.renderedHtml) {
      return this.prepareTemplateDocument(context.renderedHtml);
    }

    const watermark = this.showWatermark();
    const watermarkText = this.escapeHtml(this.t('insurance.policyDetail.draftPreview.watermark'));
    const dir = this.localization.activeCulture().toLowerCase().startsWith('fa') ? 'rtl' : 'ltr';
    const coveragesRows = context.coverages.map(row => `
      <tr>
        <td>${this.escapeHtml(row.coverageCode)}</td>
        <td>${this.escapeHtml(row.coverageName)}</td>
        <td>${this.escapeHtml(this.formatAmount(row.sumInsured))}</td>
        <td>${this.escapeHtml(this.formatAmount(row.premiumAmount))}</td>
        <td>${row.deductibleAmount == null ? '-' : this.escapeHtml(this.formatAmount(row.deductibleAmount))}</td>
      </tr>`).join('');

    const partyRows = context.partyRoles.map(row => `
      <tr>
        <td>${this.escapeHtml(this.partyLabel(row.partyId))}</td>
        <td>${this.escapeHtml(this.partyRoleLabel(row.role))}</td>
      </tr>`).join('');

    const termRows = context.terms.map(term => `
      <tr>
        <td>${term.termNumber}</td>
        <td>${this.escapeHtml(this.formatDate(term.fromUtc))}</td>
        <td>${this.escapeHtml(this.formatDate(term.toUtc))}</td>
        <td>${this.escapeHtml(this.formatAmount(term.premiumAmount))} ${this.escapeHtml(term.currencyCode)}</td>
      </tr>`).join('');

    const termsSection = context.terms.length === 0 ? '' : `
      <section>
        <h3>${this.escapeHtml(this.t('insurance.policyDetail.sections.terms'))}</h3>
        <table>
          <thead><tr><th>#</th><th>${this.escapeHtml(this.t('insurance.policies.fields.effectiveFrom'))}</th><th>${this.escapeHtml(this.t('insurance.policies.fields.effectiveTo'))}</th><th>${this.escapeHtml(this.t('insurance.policies.fields.premium'))}</th></tr></thead>
          <tbody>${termRows}</tbody>
        </table>
      </section>`;

    return `<!DOCTYPE html>
<html lang="${dir === 'rtl' ? 'fa' : 'en'}" dir="${dir}">
<head>
  <meta charset="utf-8" />
  <title>${this.escapeHtml(context.policy.policyNumber)}</title>
  ${this.buildPolicyDocumentFontHead()}
  <style>
    body { font-family: ${InsurancePolicyDraftPreviewComponent.POLICY_DOC_FONT_STACK}; margin: 24px; color: #0f172a; }
    h2 { margin: 0 0 6px; color: #1e3a8a; text-align: center; }
    .number { text-align: center; font-weight: 600; margin-bottom: 20px; }
    section { margin-top: 18px; page-break-inside: avoid; }
    h3 { margin: 0 0 8px; color: #1e40af; font-size: 15px; }
    table { width: 100%; border-collapse: collapse; font-size: 13px; }
    th, td { border: 1px solid #cbd5e1; padding: 8px; text-align: ${dir === 'rtl' ? 'right' : 'left'}; }
    th { background: #f8fafc; }
    .grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 16px; }
    .grid div { margin-bottom: 4px; }
    .label { font-size: 12px; color: #64748b; }
    .value { font-weight: 600; }
    .page { position: relative; }
    .watermark {
      position: fixed;
      inset: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 96px;
      font-weight: 700;
      color: rgba(185, 28, 28, 0.12);
      transform: rotate(-32deg);
      pointer-events: none;
      z-index: 0;
      user-select: none;
    }
    .content { position: relative; z-index: 1; }
    @media print {
      body { margin: 12mm; }
      .watermark { position: fixed; }
    }
  </style>
</head>
<body>
  <div class="page">
    ${watermark ? `<div class="watermark">${watermarkText}</div>` : ''}
    <div class="content">
      <h2>${this.escapeHtml(this.t('insurance.policyDetail.draftPreview.documentTitle'))}</h2>
      <p class="number">${this.escapeHtml(context.policy.policyNumber)}</p>
      <section>
        <h3>${this.escapeHtml(this.t('insurance.policies.sections.info'))}</h3>
        <div class="grid">
          <div><div class="label">${this.escapeHtml(this.t('columns.status'))}</div><div class="value">${this.escapeHtml(context.statusLabel)}</div></div>
          <div><div class="label">${this.escapeHtml(this.t('insurance.policies.fields.product'))}</div><div class="value">${this.escapeHtml(context.productDisplayName)}</div></div>
          <div><div class="label">${this.escapeHtml(this.t('insurance.policies.fields.lineOfBusiness'))}</div><div class="value">${this.escapeHtml(context.lineOfBusinessLabel)}</div></div>
          <div><div class="label">${this.escapeHtml(this.t('insurance.policies.fields.premium'))}</div><div class="value">${this.escapeHtml(this.formatAmount(context.policy.premiumAmount))} ${this.escapeHtml(context.policy.currencyCode)}</div></div>
          <div><div class="label">${this.escapeHtml(this.t('insurance.policies.fields.effectiveFrom'))}</div><div class="value">${this.escapeHtml(this.formatDate(context.policy.effectiveFromUtc))}</div></div>
          <div><div class="label">${this.escapeHtml(this.t('insurance.policies.fields.effectiveTo'))}</div><div class="value">${this.escapeHtml(this.formatDate(context.policy.effectiveToUtc))}</div></div>
          ${context.policy.issueDateUtc ? `<div><div class="label">${this.escapeHtml(this.t('insurance.policyDetail.fields.issueDate'))}</div><div class="value">${this.escapeHtml(this.formatDate(context.policy.issueDateUtc))}</div></div>` : ''}
        </div>
      </section>
      <section>
        <h3>${this.escapeHtml(this.t('insurance.policyDetail.sections.coverages'))}</h3>
        <table>
          <thead><tr>
            <th>${this.escapeHtml(this.t('insurance.policyDetail.coverages.code'))}</th>
            <th>${this.escapeHtml(this.t('insurance.policyDetail.coverages.name'))}</th>
            <th>${this.escapeHtml(this.t('insurance.policyDetail.coverages.sumInsured'))}</th>
            <th>${this.escapeHtml(this.t('insurance.policyDetail.coverages.premium'))}</th>
            <th>${this.escapeHtml(this.t('insurance.policyDetail.coverages.deductible'))}</th>
          </tr></thead>
          <tbody>${coveragesRows || `<tr><td colspan="5">${this.escapeHtml(this.t('insurance.policyDetail.states.noCoverages'))}</td></tr>`}</tbody>
        </table>
      </section>
      <section>
        <h3>${this.escapeHtml(this.t('insurance.policyDetail.sections.parties'))}</h3>
        <table>
          <thead><tr>
            <th>${this.escapeHtml(this.t('insurance.policyDetail.parties.partyId'))}</th>
            <th>${this.escapeHtml(this.t('insurance.policyDetail.parties.role'))}</th>
          </tr></thead>
          <tbody>${partyRows || `<tr><td colspan="2">${this.escapeHtml(this.t('insurance.policyDetail.states.noParties'))}</td></tr>`}</tbody>
        </table>
      </section>
      ${termsSection}
    </div>
  </div>
</body>
</html>`;
  }

  private formatDate(value: string | null | undefined): string {
    return this.calendar.formatDate(value, { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  private formatAmount(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '-';
    }
    return new Intl.NumberFormat(this.localization.activeCulture(), {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    }).format(value);
  }

  private buildPolicyDocumentFontHead(): string {
    const links = InsurancePolicyDraftPreviewComponent.POLICY_DOC_FONT_ASSETS
      .map(href => `<link rel="stylesheet" href="${href}" />`)
      .join('');
    const stack = InsurancePolicyDraftPreviewComponent.POLICY_DOC_FONT_STACK;
    const style = `<style>html,body,body *{font-family:${stack};}</style>`;
    return `${links}${style}`;
  }

  private prepareTemplateDocument(html: string): string {
    const trimmed = html.trim();
    if (!trimmed) {
      return trimmed;
    }

    const fontHead = this.buildPolicyDocumentFontHead();
    if (/<\/head>/i.test(trimmed)) {
      return trimmed.replace(/<\/head>/i, `${fontHead}</head>`);
    }
    if (/<head[^>]*>/i.test(trimmed)) {
      return trimmed.replace(/<head[^>]*>/i, match => `${match}${fontHead}`);
    }
    if (/<html[^>]*>/i.test(trimmed)) {
      return trimmed.replace(/<html[^>]*>/i, match => `${match}<head><meta charset="utf-8" />${fontHead}</head>`);
    }

    const dir = this.localization.activeCulture().toLowerCase().startsWith('fa') ? 'rtl' : 'ltr';
    const lang = dir === 'rtl' ? 'fa' : 'en';
    return `<!DOCTYPE html><html lang="${lang}" dir="${dir}"><head><meta charset="utf-8" />${fontHead}</head><body>${trimmed}</body></html>`;
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
